import { QueryResult, DocumentNode, OperationVariables } from "@apollo/client"
import { useQuery, UseQueryOptions } from "@oysterhr/core"
import { get } from "lodash-es"
import { useCallback } from "react"
import { CollectionMetadata } from "@oysterhr/schema"
import { useSearchParams } from "react-router-dom"

/* We don't intend to support the full useQuery interface, so we pick
   a set of options and return values we expect to be useful in a specialized pagination
   use case. If for any reason you need some extra option, they can be added ;)
   btw, onCompleted was omitted because we're not customizing its behavior to the
   pagination scenario
*/
export type UsePaginatedQueryOptions<TVariables extends OperationVariables = OperationVariables> =
  Pick<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    UseQueryOptions<any, TVariables>,
    | "onError"
    | "variables"
    | "errorPolicy"
    | "fetchPolicy"
    | "notifyOnNetworkStatusChange"
    | "skip"
    | "showErrorModal"
  > & {
    onPath: string
    perPage?: number
  }

/**
 * Sometimes we have a paginated query nested within a query.
 * The "data" object should only be used if there are 1 or 2 simple fields in the parent query.
 * If the parent query is more complex, the paginated query should be extracted and we should use two queries in parallel.
 * This avoids the parent query fields being fetched each time the paginated query is fetched.
 */

export type UsePaginatedQueryResult<
  RecordType,
  MetadataType extends CollectionMetadata = CollectionMetadata,
> = Pick<QueryResult, "error" | "loading" | "networkStatus" | "called" | "data"> & {
  paginationProps: Omit<MetadataType, "totalPages"> & { onPageChange: (page: number) => void }
  metadata: MetadataType
  collection: RecordType[]
}

const defaultMetadata: CollectionMetadata = {
  currentPage: 1,
  totalCount: 0,
  limitValue: 0,
  totalPages: 0,
}

interface PaginatableVariables {
  page?: number
  perPage?: number
}

export const usePaginatedQuery = <
  RecordType,
  TVariables extends PaginatableVariables = OperationVariables,
>(
  query: DocumentNode,
  options: UsePaginatedQueryOptions<TVariables>,
): UsePaginatedQueryResult<RecordType> => {
  const { onPath, perPage, variables } = options

  const [searchParams, setSearchParams] = useSearchParams()
  const urlPageParam = parseInt(searchParams.get("page") ?? "1")
  const page = isNaN(urlPageParam) ? 1 : Math.max(1, urlPageParam)

  options.variables = { ...(variables ?? {}), page, perPage } as TVariables
  const { loading, data, ...rest } = useQuery(query, options)

  const collectionResult = get(data, `${onPath}.collection`, undefined)
  const metadataResult = get(data, `${onPath}.metadata`, undefined)
  const collection = collectionResult ?? []
  const metadata = metadataResult ?? defaultMetadata

  if (!loading && data && !collectionResult) {
    throw new Error(
      `The usePaginatedQuery hook was unable to extract the returned collection from the query result on the path '${onPath}'.

      The returned data was:

        ${JSON.stringify(data, null, 2)}
      `,
    )
  }

  const onPageChange = useCallback(
    (page: number) => {
      if (!metadataResult) return

      const constrainedPage = Math.max(1, Math.min(page, metadataResult.totalPages))

      searchParams.set("page", String(constrainedPage))
      setSearchParams(searchParams)
    },
    [searchParams, setSearchParams, metadataResult],
  )

  const { totalPages, ...validPaginationProps } = metadata
  const paginationProps = { ...validPaginationProps, onPageChange }

  return { loading, collection, metadata, paginationProps, data, ...rest }
}
